import type { NormalizedLandmark } from "@mediapipe/tasks-vision";
import { SKELETON_CONNECTIONS, LM, type Angles } from "./angles";
import type { ContactState, ContactDetector } from "./contactDetector";

const LEFT_COLOR = "rgba(255, 255, 0, 0.9)";
const RIGHT_COLOR = "rgba(255, 0, 255, 0.9)";
const LEFT_CONTACT_COLOR = "rgba(255, 255, 0, 0.35)";
const RIGHT_CONTACT_COLOR = "rgba(255, 0, 255, 0.35)";
const BONE_COLOR = "rgba(0, 255, 128, 0.7)";
const JOINT_COLOR = "rgba(255, 255, 255, 0.8)";

export function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  landmarks: NormalizedLandmark[],
  width: number,
  height: number,
) {
  const scale = Math.min(width, height) / 500;

  // Draw connections
  ctx.strokeStyle = BONE_COLOR;
  ctx.lineWidth = Math.max(2, 3 * scale);
  for (const [i, j] of SKELETON_CONNECTIONS) {
    const a = landmarks[i];
    const b = landmarks[j];
    if (a.visibility && a.visibility < 0.3) continue;
    if (b.visibility && b.visibility < 0.3) continue;

    ctx.beginPath();
    ctx.moveTo(a.x * width, a.y * height);
    ctx.lineTo(b.x * width, b.y * height);
    ctx.stroke();
  }

  // Draw joints
  const jointRadius = Math.max(4, 6 * scale);
  const jointIndices = Object.values(LM);
  for (const idx of jointIndices) {
    const lm = landmarks[idx];
    if (lm.visibility && lm.visibility < 0.3) continue;

    ctx.fillStyle = JOINT_COLOR;
    ctx.beginPath();
    ctx.arc(lm.x * width, lm.y * height, jointRadius, 0, Math.PI * 2);
    ctx.fill();
  }
}

interface AngleLabel {
  landmark: NormalizedLandmark;
  value: number;
  label: string;
  color: string;
  offsetX: number;
}

export function drawAngles(
  ctx: CanvasRenderingContext2D,
  landmarks: NormalizedLandmark[],
  angles: Angles,
  width: number,
  height: number,
) {
  const scale = Math.min(width, height) / 500;
  const fontSize = Math.max(12, Math.round(16 * scale));
  const labels: AngleLabel[] = [];
  const offsetMag = Math.round(40 * scale);

  if (angles.hip.left != null)
    labels.push({
      landmark: landmarks[LM.LEFT_HIP],
      value: angles.hip.left,
      label: "Hip",
      color: LEFT_COLOR,
      offsetX: -offsetMag,
    });
  if (angles.hip.right != null)
    labels.push({
      landmark: landmarks[LM.RIGHT_HIP],
      value: angles.hip.right,
      label: "Hip",
      color: RIGHT_COLOR,
      offsetX: 10,
    });
  if (angles.elbow.left != null)
    labels.push({
      landmark: landmarks[LM.LEFT_ELBOW],
      value: angles.elbow.left,
      label: "Elbow",
      color: LEFT_COLOR,
      offsetX: -offsetMag,
    });
  if (angles.elbow.right != null)
    labels.push({
      landmark: landmarks[LM.RIGHT_ELBOW],
      value: angles.elbow.right,
      label: "Elbow",
      color: RIGHT_COLOR,
      offsetX: 10,
    });
  if (angles.knee.left != null)
    labels.push({
      landmark: landmarks[LM.LEFT_KNEE],
      value: angles.knee.left,
      label: "Knee",
      color: LEFT_COLOR,
      offsetX: -offsetMag,
    });
  if (angles.knee.right != null)
    labels.push({
      landmark: landmarks[LM.RIGHT_KNEE],
      value: angles.knee.right,
      label: "Knee",
      color: RIGHT_COLOR,
      offsetX: 10,
    });

  ctx.font = `bold ${fontSize}px monospace`;
  const pad = Math.round(4 * scale);
  for (const { landmark, value, color, offsetX } of labels) {
    const x = landmark.x * width + offsetX;
    const y = landmark.y * height;
    const text = `${Math.round(value)}°`;

    const metrics = ctx.measureText(text);

    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.fillRect(x - pad, y - fontSize - pad, metrics.width + pad * 2, fontSize + pad * 2 + 2);

    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  }
}

export function drawContacts(
  ctx: CanvasRenderingContext2D,
  landmarks: NormalizedLandmark[],
  contacts: ContactState,
  detector: ContactDetector,
  width: number,
  height: number,
): void {
  const scale = Math.min(width, height) / 500;
  const footRx = Math.max(18, 30 * scale);
  const footRy = Math.max(9, 15 * scale);
  const handR = Math.max(12, 21 * scale);

  if (contacts.leftFoot) {
    const pt = detector.getFootContactPoint(landmarks, "left");
    if (pt) drawEllipse(ctx, pt, width, height, footRx, footRy, LEFT_CONTACT_COLOR);
  }
  if (contacts.rightFoot) {
    const pt = detector.getFootContactPoint(landmarks, "right");
    if (pt) drawEllipse(ctx, pt, width, height, footRx, footRy, RIGHT_CONTACT_COLOR);
  }
  if (contacts.leftHand) {
    const pt = detector.getHandContactPoint(landmarks, "left");
    if (pt) drawEllipse(ctx, pt, width, height, handR, handR * 0.7, LEFT_CONTACT_COLOR);
  }
  if (contacts.rightHand) {
    const pt = detector.getHandContactPoint(landmarks, "right");
    if (pt) drawEllipse(ctx, pt, width, height, handR, handR * 0.7, RIGHT_CONTACT_COLOR);
  }
}

function drawEllipse(
  ctx: CanvasRenderingContext2D,
  pt: { x: number; y: number },
  width: number,
  height: number,
  rx: number,
  ry: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(pt.x * width, pt.y * height, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
}
